//
//  NewUserRegistration.tsx
//
//  isana.net,inc. Proprietary and Confidential
//
//  新規ユーザ向け：研究番号(管理番号)入力フォーム＋確認ダイアログ（E1-4/E1-6）。
//  「連携」→「確認」で postTenantUserInfo を呼ぶ。
//

import { type ReactElement, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { postTenantUserInfo } from "../network/googleHealth/api";
import {
  FITINSIGHT_UNIQUE_ID_MESSAGE,
  GROUP_MESSAGE,
  GROUP_SELECT,
  GROUP_USED,
} from "../utils/env";

interface Props {
  /** JWT検証で得た被験者ID（保存キー） */
  healthUserId: string;
  /**
   * E6-1: 再連携で番号を変更する場合の初期値（既存の研究番号をプリセット）。
   * 新規ユーザーでは未指定（空）。
   */
  initialFitinsightUniqueId?: string;
  /** E6-1: 再連携時の所属グループ初期値。新規では未指定（空）。 */
  initialBelongingGroup?: string;
}

const NewUserRegistration = ({
  healthUserId,
  initialFitinsightUniqueId = "",
  initialBelongingGroup = "",
}: Props): ReactElement => {
  const navigate = useNavigate();
  const [fitinsightUniqueId, setFitinsightUniqueId] = useState(
    initialFitinsightUniqueId
  );
  const [belongingGroup, setBelongingGroup] = useState(initialBelongingGroup);
  const [errorText, setErrorText] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  // 登録(POST)実行中フラグ。二重送信防止に使う。
  const [isSubmitting, setIsSubmitting] = useState(false);

  const groups = GROUP_SELECT ? GROUP_SELECT.split(",") : [];

  const showError = useCallback((msg: string) => {
    setAlertMessage(msg);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 3000);
  }, []);

  const onClickRegister = useCallback(() => {
    const trimmed = fitinsightUniqueId.trim();
    if (!trimmed) {
      setErrorText("入力してください");
      return;
    }
    setErrorText("");
    setFitinsightUniqueId(trimmed);
    setShowConfirm(true);
  }, [fitinsightUniqueId]);

  // 登録は二重送信を防ぐ。実行中は再入を禁止し「連携」ボタンも無効化する。
  // 失敗時のみ解除して再試行できるようにする（成功時は完了画面へ遷移）。
  const onClickConfirm = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setShowConfirm(false);
    postTenantUserInfo(healthUserId, fitinsightUniqueId, belongingGroup)
      .then(() => navigate("/google-health-account-linkage-complete"))
      .catch((error: Error) => {
        showError(error.message);
        setIsSubmitting(false);
      });
  }, [
    isSubmitting,
    healthUserId,
    fitinsightUniqueId,
    belongingGroup,
    navigate,
    showError,
  ]);

  return (
    <>
      <p>
        <TextField
          label={FITINSIGHT_UNIQUE_ID_MESSAGE}
          value={fitinsightUniqueId}
          onChange={(e) => setFitinsightUniqueId(e.target.value)}
          error={!!errorText}
          helperText={errorText}
        />
      </p>

      {GROUP_USED && (
        <p>
          <FormControl sx={{ m: 1, minWidth: 195 }}>
            <InputLabel id="group-label">{GROUP_MESSAGE}</InputLabel>
            <Select
              labelId="group-label"
              value={belongingGroup}
              label={GROUP_MESSAGE}
              autoWidth
              onChange={(e) => setBelongingGroup(e.target.value)}
            >
              {groups.map((item, index) => (
                <MenuItem key={index} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </p>
      )}

      <Button
        variant="contained"
        onClick={onClickRegister}
        disabled={(GROUP_USED && belongingGroup === "") || isSubmitting}
      >
        {isSubmitting ? "連携中..." : "連携する"}
      </Button>

      {showAlert && (
        <Box sx={{ mt: 2, mx: "auto", maxWidth: 360, width: "fit-content" }}>
          <Alert severity="error" onClose={() => setShowAlert(false)}>
            {alertMessage}
          </Alert>
        </Box>
      )}

      <Dialog open={showConfirm} onClose={() => setShowConfirm(false)}>
        <DialogTitle>確認</DialogTitle>
        <DialogContent>
          <Typography>
            {FITINSIGHT_UNIQUE_ID_MESSAGE}は{" "}
            <Typography color="primary" component="span">
              {fitinsightUniqueId}
            </Typography>{" "}
            です。
            {GROUP_USED && (
              <>
                {GROUP_MESSAGE}は{" "}
                <Typography color="secondary" component="span">
                  {belongingGroup}
                </Typography>{" "}
                です。
              </>
            )}
            お間違いありませんか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirm(false)} color="primary">
            キャンセル
          </Button>
          <Button
            onClick={onClickConfirm}
            color="primary"
            disabled={isSubmitting}
          >
            確認
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NewUserRegistration;
